import { DateTime } from 'luxon';
import { parseDateTimeToTimestamp } from './dateUtils';

export type SortDirection = 'asc' | 'desc';
// Deliberately Record<string, unknown>, not a narrower value union: real row DTOs (e.g.
// FlightSchedule) have plenty of fields (arrays, nested objects) that are never sorted on.
// Only the value at the requested `key` needs to be one of the types compareRowValues
// actually knows how to compare, and that's checked at runtime via typeof/instanceof below.
export type SortableRow = Record<string, unknown>;

// Table.tsx renders some date-like columns as pre-formatted "dd.MM.yyyy HH:mm" strings
// rather than Date objects; this recovers a comparable Date from that format so those
// columns still sort chronologically instead of lexicographically.
const parseStringToDate = (dateString: string): Date | null => {
    const luxonDate = DateTime.fromFormat(dateString, 'dd.MM.yyyy HH:mm');
    return luxonDate.isValid ? luxonDate.toJSDate() : null;
};

// Other columns (notably FlightSchedule.arrivalTime/departureTime, typed `Date?` but delivered
// as ISO strings by fetch().json()) never match the MXI format above. Deliberately anchored on a
// literal ISO date prefix so it can only ever fire on real timestamps - values like "FI614 JFK"
// or a bare number must keep falling through to localeCompare.
const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

const parseIsoStringToTimestamp = (dateString: string): number | null => {
    if (!ISO_DATE_PREFIX.test(dateString)) return null;
    const timestamp = parseDateTimeToTimestamp(dateString);
    return Number.isFinite(timestamp) ? timestamp : null;
};

// Recovers a timestamp from either representation a date column can hold. Used only for the
// mixed Date/string case below, where the typed branches can't help.
const toTimestamp = (value: unknown): number | null => {
    if (value instanceof Date) {
        const timestamp = value.getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
    }
    if (typeof value !== 'string') return null;
    const mxiDate = parseStringToDate(value);
    return mxiDate !== null ? mxiDate.getTime() : parseIsoStringToTimestamp(value);
};

// Compares two rows on a single key. Kept private so compareRowValues stays the one public
// entry point and can layer the secondary-key tiebreaker on top of a finished comparison.
const compareSingleKey = (
    a: SortableRow,
    b: SortableRow,
    key: string,
    direction: SortDirection,
    isFlightSchedule: boolean,
): number => {
    let comparison = 0;
    const valueA = a[key];
    const valueB = b[key];

    // Null/undefined values always sort last, regardless of direction, so "unknown"
    // rows (e.g. no arrival time yet) stay predictably grouped at the bottom instead
    // of falling through every typed branch below and being treated as "equal".
    const aIsNullish = valueA === null || valueA === undefined;
    const bIsNullish = valueB === null || valueB === undefined;
    if (aIsNullish || bIsNullish) {
        if (aIsNullish && bIsNullish) return 0;
        return aIsNullish ? 1 : -1; // not scaled by `direction` - intentionally direction-independent
    }

    if (isFlightSchedule && key === 'currentAirport') {
        const aIsKef = valueA === 'KEF' ? 1 : 0;
        const bIsKef = valueB === 'KEF' ? 1 : 0;
        if (aIsKef !== bIsKef) return direction === 'asc' ? bIsKef - aIsKef : aIsKef - bIsKef;
    }

    if (typeof valueA === 'number' && typeof valueB === 'number') {
        comparison = valueA - valueB;
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
        const dateA = parseStringToDate(valueA);
        const dateB = parseStringToDate(valueB);
        if (dateA && dateB) {
            comparison = dateA.getTime() - dateB.getTime();
        } else {
            const isoA = parseIsoStringToTimestamp(valueA);
            const isoB = parseIsoStringToTimestamp(valueB);
            if (isoA !== null && isoB !== null) comparison = isoA - isoB;
            else comparison = valueA.localeCompare(valueB);
        }
    } else if (valueA instanceof Date && valueB instanceof Date) {
        comparison = valueA.getTime() - valueB.getTime();
    } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
        comparison = Number(valueA) - Number(valueB);
    } else if (valueA instanceof Date !== valueB instanceof Date) {
        // One Date, one string: a single column can hold both, e.g. certificate rights arrive
        // from the backend as date strings while a row that was just added or edited in the UI
        // holds a real Date. Neither typed branch above matches, so those rows used to tie and
        // the odd one out never moved when the column was sorted. Falls through to a tie if
        // either side isn't recoverable as a timestamp.
        const timeA = toTimestamp(valueA);
        const timeB = toTimestamp(valueB);
        if (timeA !== null && timeB !== null) comparison = timeA - timeB;
    }

    return direction === 'asc' ? comparison : -comparison;
};

// Single source of truth for "compare two row values by key" - shared by Table.tsx's
// dynamicSort (header-click sorting) and any page that needs to apply the same sort logic
// to freshly-fetched data before it ever reaches the table (e.g. a default sort on load).
//
// `secondaryKey` is an opt-in tiebreaker: when the primary comparison is a draw, rows are
// ordered by that key *always ascending*, independent of the primary column's direction.
// That's why it's applied after compareSingleKey (which has already negated for `desc`) -
// e.g. sorting FlightSchedule by station descending still lists each station's flights
// earliest-arrival first. Omit it and the comparison is byte-identical to a single-key sort.
export const compareRowValues = (
    a: SortableRow,
    b: SortableRow,
    key: string,
    direction: SortDirection,
    isFlightSchedule = false,
    secondaryKey?: string,
): number => {
    const primary = compareSingleKey(a, b, key, direction, isFlightSchedule);
    if (primary !== 0 || secondaryKey === undefined || secondaryKey === key) return primary;
    // A column that isn't a field on the row data at all (the flight tables' `index`, `mechanics`
    // and `tsr` headers) must stay the no-op it was, not silently reorder the table by the
    // tiebreaker. Keyed on field *presence*, not value: a field that exists but is empty (blank
    // remarks, blank stand) still gets the arrival tiebreaker, which is the point of the feature.
    if (!(key in a) && !(key in b)) return primary;
    return compareSingleKey(a, b, secondaryKey, 'asc', isFlightSchedule);
};

// Unconstrained T on purpose: callers pass concrete DTOs (e.g. FlightSchedule) that have no
// index signature, so they can't structurally satisfy `extends SortableRow`. compareRowValues
// only ever reads the single `key` field at runtime, so the cast below is safe.
export const sortRowsByKey = <T,>(
    rows: T[],
    key: string,
    direction: SortDirection,
    isFlightSchedule = false,
    secondaryKey?: string,
): T[] => [...rows].sort((a, b) =>
    compareRowValues(a as unknown as SortableRow, b as unknown as SortableRow, key, direction, isFlightSchedule, secondaryKey));
